import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import { useLanguage } from '../../contexts/LanguageContext';
import { smoothScrollTo } from '../../utils/smoothScroll';
import Button from '../ui/Button';
import '../../styles/components/Hero.css';

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const [particlesReady, setParticlesReady] = useState(false);
  const heroRef = useRef<HTMLElement>(null);


  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Initialize tsparticles engine
  useEffect(() => {
    const hasPointer = window.matchMedia('(pointer: fine)').matches;
    if (!hasPointer) return;

    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setParticlesReady(true);
    });
  }, []);

  const scrollToTeam = () => {
    const el = document.getElementById('team');
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      smoothScrollTo(top, 700);
    }
  };

  const scrollToAbout = () => {
    const el = document.getElementById('about');
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      smoothScrollTo(top, 700);
    }
  };

  const particles = useMemo(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 4 + 2,
      delay: Math.random() * 5,
      duration: Math.random() * 10 + 10,
      tx: (Math.random() - 0.5) * 200,
      ty: (Math.random() - 0.5) * 200,
    })), []);

  const particlesOptions = useMemo(() => ({
    fullScreen: { enable: false },
    fpsLimit: 60,
    particles: {
      number: {
        value: 30,
        density: { enable: true, width: 1000, height: 1000 },
      },
      color: { value: '#f472b6' },
      shape: { type: 'circle' },
      opacity: {
        value: { min: 0.05, max: 0.35 },
        animation: {
          enable: true,
          speed: 0.5,
          startValue: 'random',
          sync: false,
        },
      },
      size: {
        value: { min: 1, max: 4 },
      },
      move: {
        enable: true,
        speed: 0.4,
        direction: 'none' as const,
        random: true,
        straight: false,
        outModes: { default: 'out' as const },
      },
    },
    interactivity: {
      detectsOn: 'canvas' as const,
      events: {
        onHover: {
          enable: true,
          mode: 'grab',
        },
      },
      modes: {
        grab: {
          distance: 220,
          links: {
            opacity: 0.5,
            color: '#f472b6',
          },
        },
      },
    },
    detectRetina: true,
  }), []);

  const teamName = t('hero.teamName');

  return (
    <section id="hero" className="hero" ref={heroRef}>
      {/* Interactive particles effect */}
      {particlesReady && (
        <Particles
          className="hero__particles-canvas"
          options={particlesOptions as any}
        />
      )}

      {/* Particle background */}
      <div className="hero__particles" aria-hidden="true">
        {particles.map(p => (
          <div
            key={p.id}
            className="hero__particle"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              ['--tx' as string]: `${p.tx}px`,
              ['--ty' as string]: `${p.ty}px`,
            }}
          />
        ))}
      </div>

      {/* Gradient mesh background */}
      <div className="hero__gradient" aria-hidden="true" />

      {/* Grid lines */}
      <div className="hero__grid" aria-hidden="true" />

      {/* Content */}
      <div className={`hero__content ${isLoaded ? 'hero__content--loaded' : ''}`}>
        <p className="hero__greeting">{t('hero.greeting')}</p>

        <h1 className="hero__title">
          {teamName.split('').map((letter, i) => {
            const isPlus = letter === '+';
            return (
              <span
                key={i}
                className={`hero__letter ${isPlus ? 'hero__letter--plus' : ''}`}
                style={{ animationDelay: `${600 + i * 80}ms` }}
              >
                {letter}
              </span>
            );
          })}
        </h1>

        <p className="hero__tagline">{t('hero.tagline')}</p>

        <div className="hero__cta">
          <Button variant="primary" size="lg" onClick={scrollToTeam}>
            {t('hero.cta')}
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <button className="hero__scroll-indicator" onClick={scrollToAbout} aria-label={t('hero.scrollDown')}>
        <span className="hero__scroll-text">{t('hero.scrollDown')}</span>
        <ChevronDown size={20} className="hero__scroll-icon" />
      </button>
    </section>
  );
};

export default Hero;
